/**
 * Old→new detail diff for one changed IFC element (WHA-3695). Fetches both
 * sides' full properties via the existing get_ifc_element_properties RPC and
 * renders the structural diff from `diffElementProperties`: changed attributes,
 * Pset/Qto properties ("measurements") and material assignments, each as a
 * Before → After pair, with added/removed rows flagged.
 */

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight } from "lucide-react";

import { useLanguage } from "@/lib/i18n/LanguageContext";
import {
  fetchIfcElementProperties,
  type IfcElementDetails,
  type IfcElementProperty,
  type IfcStoreyRef,
} from "@/stores/ifcModel/repository";
import type { BuildingIfcViewerSections } from "@/lib/i18n/types/buildings-sections/ifc-viewer";
import {
  diffElementProperties,
  diffJsonValues,
  type AttributeField,
  type DiffStatus,
  type ElementDiff,
  type JsonScalar,
  type PropertyDiffRow,
} from "./diffElementProperties";
import { formatThicknessMm } from "./formatThicknessMm";

interface IfcElementDiffDetailProps {
  baseDocId: number;
  targetDocId: number;
  guid: string;
  /** Which kind of change this element is — added/removed elements show only
   * the present side; modified shows both. */
  changeType: "added" | "removed" | "modified";
  /** Storey lookup from the diff payload — maps storey/parent GUIDs in the
   * containment attribute rows to human-readable storey names. Optional: rows
   * fall back to the raw GUID without it. */
  storeys?: IfcStoreyRef[];
}

export function IfcElementDiffDetail({
  baseDocId,
  targetDocId,
  guid,
  changeType,
  storeys,
}: IfcElementDiffDetailProps) {
  const { t } = useLanguage();
  const tv = t.buildings.ifcViewer;

  // Element properties are immutable for a given (document, element), so both
  // sides cache forever (staleTime: Infinity) — clicking back and forth between
  // elements re-reads the cache instead of re-firing the RPCs (WHA-3756). Added
  // elements have no base side, removed have no target; that query stays
  // disabled and contributes a null side to the diff.
  const needsBase = changeType !== "added";
  const needsTarget = changeType !== "removed";

  const baseQuery = useQuery({
    queryKey: ["ifc-element-properties", baseDocId, guid],
    queryFn: () => fetchIfcElementProperties(baseDocId, guid),
    enabled: needsBase,
    staleTime: Infinity,
  });
  const targetQuery = useQuery({
    queryKey: ["ifc-element-properties", targetDocId, guid],
    queryFn: () => fetchIfcElementProperties(targetDocId, guid),
    enabled: needsTarget,
    staleTime: Infinity,
  });

  const isLoading = (needsBase && baseQuery.isLoading) || (needsTarget && targetQuery.isLoading);
  const isError = (needsBase && baseQuery.isError) || (needsTarget && targetQuery.isError);

  const diff = useMemo<ElementDiff | null>(() => {
    if (isLoading || isError) return null;
    return diffElementProperties(
      needsBase ? (baseQuery.data ?? null) : null,
      needsTarget ? (targetQuery.data ?? null) : null
    );
  }, [isLoading, isError, needsBase, needsTarget, baseQuery.data, targetQuery.data]);

  if (isLoading) {
    return <p className="px-3 py-2 text-xs text-muted-foreground">{tv.diffLoadingDetail}</p>;
  }
  if (isError) {
    return <p className="px-3 py-2 text-xs text-muted-foreground">{tv.loadPropertiesFailed}</p>;
  }
  if (!diff) return null;

  const empty =
    diff.attributes.length === 0 && diff.properties.length === 0 && diff.materials.length === 0;
  if (empty) {
    return <p className="px-3 py-2 text-xs text-muted-foreground">{tv.diffNoDetail}</p>;
  }

  const attrLabel: Record<AttributeField, string> = {
    type: tv.propType,
    name: tv.propName,
    long_name: tv.propLongName,
    elevation: tv.propElevation,
    storey: tv.diffAttrStorey,
    parent: tv.diffAttrParent,
  };

  // Containment rows carry raw GUIDs; show the storey name where we know it
  // (parents are often storeys too — non-storey parents keep the GUID).
  const storeyName = new Map((storeys ?? []).map((s) => [s.guid, s.name ?? s.guid]));
  const attrValue = (field: AttributeField, v: string | null): string | null =>
    v != null && (field === "storey" || field === "parent") ? (storeyName.get(v) ?? v) : v;

  return (
    <div className="flex flex-col gap-3 px-3 py-2 text-xs">
      {diff.attributes.length > 0 && (
        <DiffSection title={tv.diffAttributesHeader}>
          {diff.attributes.map((a) => (
            <DiffRow
              key={a.field}
              label={attrLabel[a.field]}
              before={attrValue(a.field, a.before)}
              after={attrValue(a.field, a.after)}
              status={a.before == null ? "added" : a.after == null ? "removed" : "changed"}
              tv={tv}
            />
          ))}
        </DiffSection>
      )}

      {diff.properties.length > 0 && (
        <DiffSection title={tv.diffPropertiesHeader}>
          {diff.properties.map((p) => (
            <PropertyDiffEntry key={p.key} row={p} tv={tv} />
          ))}
        </DiffSection>
      )}

      {diff.materials.length > 0 && (
        <DiffSection title={tv.diffMaterialsHeader}>
          {diff.materials.map((m) => (
            <DiffRow
              key={m.key}
              label={
                m.layer_index != null ? `${m.name} · ${tv.layerWord} ${m.layer_index}` : m.name
              }
              before={
                m.before ? formatMaterial(m.before.layer_thickness, m.before.relation_kind) : null
              }
              after={
                m.after ? formatMaterial(m.after.layer_thickness, m.after.relation_kind) : null
              }
              status={m.status}
              tv={tv}
            />
          ))}
        </DiffSection>
      )}
    </div>
  );
}

function DiffSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {title}
      </p>
      <div className="flex flex-col gap-1">{children}</div>
    </div>
  );
}

/** One Before → After line. Added rows show only the after value (green),
 * removed only the before (red, struck), changed shows both. */
function DiffRow({
  label,
  sublabel,
  before,
  after,
  status,
  tv,
}: {
  label: string;
  sublabel?: string;
  before: string | null;
  after: string | null;
  status: DiffStatus;
  tv: BuildingIfcViewerSections;
}) {
  return (
    <div className="rounded border border-border/60 px-2 py-1">
      <div className="flex items-baseline justify-between gap-2">
        <span className="truncate font-medium">{label}</span>
        {sublabel && <span className="shrink-0 text-[10px] text-muted-foreground">{sublabel}</span>}
      </div>
      <div className="mt-0.5 flex items-center gap-1.5">
        {status !== "added" && (
          <span className="truncate text-rose-600 line-through dark:text-rose-400">
            {before ?? "—"}
          </span>
        )}
        {status === "changed" && <span className="text-muted-foreground">→</span>}
        {status !== "removed" && (
          <span className="truncate text-emerald-600 dark:text-emerald-400">{after ?? "—"}</span>
        )}
        <span className="ml-auto shrink-0 text-[10px] uppercase text-muted-foreground">
          {statusWord(status, tv)}
        </span>
      </div>
    </div>
  );
}

/**
 * One changed property. Scalar values (text/number/bool) render as a plain
 * Before → After line. Complex-quantity values (value_json — wall/slab layers)
 * render as a collapsed chip summary of what moved that expands to the full,
 * stylized key-value layer (WHA-3695).
 */
function PropertyDiffEntry({ row, tv }: { row: PropertyDiffRow; tv: BuildingIfcViewerSections }) {
  const isJson = row.before?.value_json != null || row.after?.value_json != null;
  if (!isJson) {
    return (
      <DiffRow
        label={row.property_name}
        sublabel={row.pset_name}
        before={row.before ? formatPropertyValue(row.before, tv) : null}
        after={row.after ? formatPropertyValue(row.after, tv) : null}
        status={row.status}
        tv={tv}
      />
    );
  }
  return (
    <JsonDiffRow
      label={row.property_name}
      sublabel={row.pset_name}
      beforeJson={row.before?.value_json ?? null}
      afterJson={row.after?.value_json ?? null}
      status={row.status}
      tv={tv}
    />
  );
}

/** Collapsible complex-quantity diff: compact chips of the changed fields,
 * expanding to the full key-value layer with Before → After styling. */
function JsonDiffRow({
  label,
  sublabel,
  beforeJson,
  afterJson,
  status,
  tv,
}: {
  label: string;
  sublabel?: string;
  beforeJson: unknown;
  afterJson: unknown;
  status: DiffStatus;
  tv: BuildingIfcViewerSections;
}) {
  const [open, setOpen] = useState(false);
  const fields = useMemo(() => diffJsonValues(beforeJson, afterJson), [beforeJson, afterJson]);
  const moved = fields.filter((f) => f.status !== "same");

  return (
    <div className="rounded border border-border/60">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-baseline gap-1.5 px-2 py-1 text-left"
      >
        {open ? (
          <ChevronDown className="size-3 shrink-0 translate-y-0.5 text-muted-foreground" />
        ) : (
          <ChevronRight className="size-3 shrink-0 translate-y-0.5 text-muted-foreground" />
        )}
        <span className="truncate font-medium">{label}</span>
        {sublabel && (
          <span className="ml-auto shrink-0 text-[10px] text-muted-foreground">{sublabel}</span>
        )}
        <span className="shrink-0 text-[10px] uppercase text-muted-foreground">
          {statusWord(status, tv)}
        </span>
      </button>

      {open ? (
        <div className="flex flex-col gap-0.5 px-2 pb-1.5 pl-6">
          {fields.map((f) => (
            <div key={f.key} className="flex items-center gap-1.5">
              <span className="w-24 shrink-0 truncate text-[10px] text-muted-foreground">
                {f.key || tv.value}
              </span>
              {f.status === "same" ? (
                <span className="truncate">{formatScalar(f.after, tv)}</span>
              ) : (
                <>
                  {f.status !== "added" && (
                    <span className="truncate text-rose-600 line-through dark:text-rose-400">
                      {formatScalar(f.before, tv)}
                    </span>
                  )}
                  {f.status === "changed" && <span className="text-muted-foreground">→</span>}
                  {f.status !== "removed" && (
                    <span className="truncate text-emerald-600 dark:text-emerald-400">
                      {formatScalar(f.after, tv)}
                    </span>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-1 px-2 pb-1 pl-6">
          {moved.map((f) => (
            <span key={f.key} className="rounded bg-muted px-1 py-0.5 text-[10px] leading-tight">
              {f.key && <span className="text-muted-foreground">{f.key} </span>}
              {f.status !== "added" && (
                <span className="text-rose-600 line-through dark:text-rose-400">
                  {formatScalar(f.before, tv)}
                </span>
              )}
              {f.status === "changed" && <span className="text-muted-foreground"> → </span>}
              {f.status !== "removed" && (
                <span className="text-emerald-600 dark:text-emerald-400">
                  {formatScalar(f.after, tv)}
                </span>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function statusWord(status: DiffStatus, tv: BuildingIfcViewerSections): string {
  return status === "added"
    ? tv.changeAdded
    : status === "removed"
      ? tv.changeRemoved
      : tv.changeModified;
}

function formatNumber(n: number): string {
  const abs = Math.abs(n);
  if (abs !== 0 && (abs >= 10000 || abs < 0.01)) return n.toExponential(2);
  // Trim trailing zeros so layer widths read "12.5", not "12.50".
  return String(Number(n.toFixed(2)));
}

/** Format a flattened json field value (number/bool/string) for display. */
function formatScalar(v: JsonScalar | null, tv: BuildingIfcViewerSections): string {
  if (v == null) return "—";
  if (typeof v === "number") return formatNumber(v);
  if (typeof v === "boolean") return v ? tv.yes : tv.no;
  return v;
}

/** Scalar property value formatting (text/number/bool). value_json is handled by
 * JsonDiffRow; the json branch here is a defensive fallback only. */
function formatPropertyValue(p: IfcElementProperty, tv: BuildingIfcViewerSections): string {
  if (p.value_text != null) return p.value_text;
  if (p.value_number != null) return formatNumber(Number(p.value_number));
  if (p.value_bool != null) return p.value_bool ? tv.yes : tv.no;
  if (p.value_json != null) {
    const fields = diffJsonValues(null, p.value_json).filter((f) => f.status !== "same");
    return fields.map((f) => `${f.key}: ${formatScalar(f.after, tv)}`).join(", ");
  }
  return "—";
}

function formatMaterial(thickness: number | null, relationKind: string): string {
  // layer_thickness is stored in metres; formatThicknessMm converts. The old
  // `${thickness.toFixed(0)} mm` rendered the raw metre value as mm ("0 mm").
  if (thickness != null) return formatThicknessMm(Number(thickness));
  return relationKind;
}
